/**
 * ModeToggle — switches between "vulnerable" and "CSK Protected" demo modes.
 *
 * Single responsibility: render the toggle UI and call onChange.
 * No state — controlled component pattern.
 *
 * The slider knob is driven by `motion`'s `layout` animation with spring
 * physics, replacing the older CSS `transition: left 0.2s` so the
 * tactile feel matches the rest of the motion suite. The track color
 * also animates so the WARN→SAFE handoff reads as a single morph
 * rather than two property changes. Both honor `prefers-reduced-motion`.
 *
 * Labels are paired with `lucide-react` icons (AlertTriangle for
 * "Vulnerable", ShieldCheck for "CSK Protected") instead of Unicode
 * emoji so the surface renders identically on every platform and the
 * icons inherit `currentColor` from the label.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { motion, useReducedMotion } from "motion/react";
import { AlertTriangle, ShieldCheck } from "lucide-react";
import type { DemoMode } from "../types.js";
import { SAFE, WARN, GRAY } from "../design-system/tokens.js";
import { motionDurations, motionEasings } from "../styling/motionTokens.js";

export interface ModeToggleProps {
  readonly mode: DemoMode;
  readonly onChange: (mode: DemoMode) => void;
}

/**
 * Renders a labeled toggle switch between "Vulnerable" and "CSK Protected".
 * Accessible via button with aria-label and aria-pressed.
 */
export const ModeToggle = React.memo(function ModeToggle({
  mode,
  onChange,
}: ModeToggleProps): React.JSX.Element {
  const isCSK = mode === "csk";
  const trackColor = isCSK ? SAFE[500] : WARN[500];
  const prefersReducedMotion = useReducedMotion() ?? false;
  const labelStyle = (active: boolean): React.CSSProperties => ({
    fontWeight: "500",
    color: active ? (isCSK ? SAFE[700] : WARN[700]) : GRAY[500],
    // Reduced motion gets a hard color swap; everyone else gets the
    // CSS color tween that makes the WARN ↔ SAFE handoff feel smooth.
    transition: prefersReducedMotion ? "none" : "color 0.2s",
    display: "inline-flex",
    alignItems: "center",
    gap: "0.375rem",
  });
  // The slider sits in one of two slots; `motion` layout-animates the
  // knob between them. Spring physics gives the toggle a tactile feel
  // without bespoke keyframes; reduced-motion collapses to a tween of
  // duration 0 so the layout snap is instantaneous.
  const knobTransition = prefersReducedMotion
    ? { duration: 0 }
    : { type: "spring" as const, stiffness: 700, damping: 32 };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "1rem",
        padding: "1rem",
        backgroundColor: "white",
        borderRadius: "0.5rem",
        border: `1px solid ${GRAY[300]}`,
        marginBottom: "1.5rem",
      }}
    >
      <span style={labelStyle(!isCSK)}>
        <AlertTriangle aria-hidden="true" size={16} />
        <span>Vulnerable</span>
      </span>

      <motion.button
        type="button"
        onClick={(): void => {
          onChange(isCSK ? "vulnerable" : "csk");
        }}
        aria-label={`Switch to ${isCSK ? "vulnerable" : "CSK protected"} mode`}
        aria-pressed={isCSK}
        animate={{ backgroundColor: trackColor }}
        transition={{
          duration: prefersReducedMotion ? 0 : motionDurations.morph / 2,
          ease: motionEasings.standard,
        }}
        style={{
          position: "relative",
          width: "4rem",
          height: "2rem",
          borderRadius: "9999px",
          backgroundColor: trackColor,
          border: "none",
          cursor: "pointer",
          padding: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: isCSK ? "flex-end" : "flex-start",
        }}
      >
        <motion.span
          aria-hidden="true"
          layout
          transition={knobTransition}
          data-testid="mode-toggle-knob"
          style={{
            width: "1.5rem",
            height: "1.5rem",
            margin: "0 0.25rem",
            backgroundColor: "white",
            borderRadius: "50%",
            boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
          }}
        />
      </motion.button>

      <span style={labelStyle(isCSK)}>
        <ShieldCheck aria-hidden="true" size={16} />
        <span>CSK Protected</span>
      </span>
    </div>
  );
});
